import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TopBar } from "../components/TopBar";
import { Card, ErrorText, Field, InviteLinkBox, Section, btnPrimary, btnSecondary, inputCls } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { inviteUrlFrom } from "../lib/session";
import { isInContract, type Invitation, type Org } from "../lib/types";
import { levelLabel, shortDate, visibleEmail } from "../lib/format";

type Teacher = { id: number; name: string; email?: string; status?: string | null };
type SchoolClass = { id: number; name: string; teacher_id?: number | null; teacher_name?: string | null; learner_count?: number; student_count?: number };
type Learner = {
  id: number; login_id?: string | null; display_name?: string; name?: string;
  status?: string | null; class_id?: number | null; class_name?: string | null;
};
type Credential = { login_id: string; display_name: string; password: string; class?: string | null };
type ProgressRow = {
  class_id?: number | null; class_name?: string | null; name?: string | null;
  learner_count?: number; avg_level?: number | string | null; avg_level_label?: string | null; last_studied_at?: string | null;
};
type ProgressRes = ProgressRow[] | { classes?: ProgressRow[]; total?: ProgressRow | null };

const errMsg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export function SchoolPage() {
  const { t } = useTranslation(["common", "admin"]);
  const queryClient = useQueryClient();
  const refresh = (...keys: string[]) => keys.forEach((k) => void queryClient.invalidateQueries({ queryKey: [k] }));

  // 自校の概要（口が無ければ出さない）
  const org = useQuery({ queryKey: ["school-org"], queryFn: () => api<Org>("/api/org-admin/org"), retry: false });
  const teachers = useQuery({ queryKey: ["school-teachers"], queryFn: () => api<Teacher[]>("/api/org-admin/teachers") });
  const classes = useQuery({ queryKey: ["school-classes"], queryFn: () => api<SchoolClass[]>("/api/org-admin/classes") });
  const learners = useQuery({ queryKey: ["school-learners"], queryFn: () => api<Learner[]>("/api/org-admin/learners") });
  const progress = useQuery({ queryKey: ["school-progress"], queryFn: () => api<ProgressRes>("/api/org-admin/progress") });

  // 先生の招待・停止
  const [teacherInvite, setTeacherInvite] = useState<string | null>(null);
  const inviteTeacher = useMutation({
    mutationFn: () => api<Invitation>("/api/org-admin/invitations", { method: "POST", body: {} }),
    onSuccess: (d) => setTeacherInvite(inviteUrlFrom(d)),
  });
  const setTeacherStatus = useMutation({
    mutationFn: (v: { id: number; status: string }) => api(`/api/org-admin/teachers/${v.id}`, { method: "PATCH", body: { status: v.status } }),
    onSuccess: () => refresh("school-teachers"),
  });

  // クラス
  const [className, setClassName] = useState("");
  const [classTeacher, setClassTeacher] = useState("");
  const createClass = useMutation({
    mutationFn: (v: { name: string; teacher_id: number }) => api("/api/org-admin/classes", { method: "POST", body: v }),
    onSuccess: () => {
      setClassName("");
      setClassTeacher("");
      refresh("school-classes", "school-progress");
    },
  });

  // 学習者 CSV
  const [csv, setCsv] = useState("");
  const [credentials, setCredentials] = useState<Credential[] | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  const uploadCsv = useMutation({
    mutationFn: (text: string) => api<{ credentials: Credential[] }>("/api/org-admin/learners/csv", { method: "POST", body: { csv: text } }),
    onMutate: () => { setCsvError(null); setCredentials(null); },
    onSuccess: (d) => {
      setCredentials(d?.credentials ?? []);
      setCsv("");
      refresh("school-learners", "school-classes", "school-progress", "school-org");
    },
    onError: (e) => setCsvError(errMsg(e, t("common:error_generic"))),
  });
  const updateLearner = useMutation({
    mutationFn: (v: { id: number; body: { status?: string; class_id?: number | null } }) =>
      api(`/api/org-admin/learners/${v.id}`, { method: "PATCH", body: v.body }),
    onSuccess: () => refresh("school-learners", "school-classes", "school-progress", "school-org"),
  });

  const activeTeachers = (teachers.data ?? []).filter((x) => x.status !== "suspended");
  const progressRows: ProgressRow[] = Array.isArray(progress.data) ? progress.data : progress.data?.classes ?? [];
  const progressTotal = Array.isArray(progress.data) ? null : progress.data?.total ?? null;
  const orgInContract = org.data ? ((org.data as Org & { in_contract?: boolean }).in_contract ?? isInContract(org.data)) : true;

  function downloadCredentials() {
    if (!credentials) return;
    const esc = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const lines = ["login_id,display_name,class,password", ...credentials.map((c) => [c.login_id, c.display_name, c.class ?? "", c.password].map(esc).join(","))];
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `learners_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar subtitle={org.data?.name ?? t("common:role_org_admin")} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs text-muted">{t("admin:school_title")}</div>
            <h1 className="text-2xl font-bold text-primary">{org.data?.name ?? t("admin:school_title")}</h1>
          </div>
          {org.data && (
            <div className="text-sm text-muted text-right">
              <div>
                {t("admin:school_cap_line", { n: org.data.learner_count ?? 0, cap: org.data.learner_cap ?? t("admin:org_no_cap") })}
              </div>
              <div>
                {t("admin:org_period")}：
                {org.data.contract_starts_on || org.data.contract_ends_on
                  ? `${org.data.contract_starts_on ?? ""} 〜 ${org.data.contract_ends_on ?? ""}`
                  : t("admin:org_no_period")}
              </div>
            </div>
          )}
        </div>
        {org.data && !orgInContract && (
          <div className="mt-4"><ErrorText>{t("admin:school_contract_off")}</ErrorText></div>
        )}

        {/* 先生 */}
        <Section
          title={t("admin:school_teachers")}
          actions={
            <button data-testid="teacher-invite" type="button" disabled={inviteTeacher.isPending}
              onClick={() => inviteTeacher.mutate()} className={btnPrimary}>
              {t("admin:school_teacher_invite")}
            </button>
          }
        >
          {teacherInvite && <div className="mb-3"><InviteLinkBox url={teacherInvite} /></div>}
          {inviteTeacher.error && <div className="mb-3"><ErrorText>{errMsg(inviteTeacher.error, t("common:error_generic"))}</ErrorText></div>}
          <Card className="overflow-x-auto">
            {teachers.isLoading && <p className="text-muted p-4">{t("common:loading")}</p>}
            {teachers.error && <div className="p-4"><ErrorText>{errMsg(teachers.error, t("common:error_generic"))}</ErrorText></div>}
            {teachers.data && teachers.data.length === 0 && <p className="text-muted p-4 text-sm">{t("admin:school_no_teachers")}</p>}
            {teachers.data && teachers.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">{t("admin:col_name")}</th>
                    <th className="text-left px-4 py-2">{t("common:email")}</th>
                    <th className="text-left px-4 py-2">{t("admin:org_status")}</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {teachers.data.map((x) => {
                    const suspended = x.status === "suspended";
                    return (
                      <tr key={x.id} data-testid="teacher-row" className="border-b border-gray-100">
                        <td className="px-4 py-2 font-bold text-primary">{x.name}</td>
                        <td className="px-4 py-2 text-muted">{visibleEmail(x.email)}</td>
                        <td className="px-4 py-2">{suspended ? <span className="text-accent">{t("common:status_suspended")}</span> : t("common:status_active")}</td>
                        <td className="px-4 py-2 text-right">
                          <button type="button" className={btnSecondary} disabled={setTeacherStatus.isPending}
                            onClick={() => setTeacherStatus.mutate({ id: x.id, status: suspended ? "active" : "suspended" })}>
                            {suspended ? t("admin:school_resume") : t("admin:school_suspend")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
          {setTeacherStatus.error && <div className="mt-3"><ErrorText>{errMsg(setTeacherStatus.error, t("common:error_generic"))}</ErrorText></div>}
        </Section>

        {/* クラス */}
        <Section title={t("admin:school_classes")}>
          <Card className="p-4">
            <form
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end"
              onSubmit={(e) => {
                e.preventDefault();
                const tid = Number(classTeacher);
                if (!className.trim() || !tid) return;
                createClass.mutate({ name: className.trim(), teacher_id: tid });
              }}
            >
              <Field label={t("admin:school_class_name")}>
                <input data-testid="class-form-name" type="text" required maxLength={80} value={className}
                  onChange={(e) => setClassName(e.target.value)} className={inputCls} />
              </Field>
              <Field label={t("admin:school_class_teacher")}>
                <select data-testid="class-form-teacher" required value={classTeacher}
                  onChange={(e) => setClassTeacher(e.target.value)} className={inputCls}>
                  <option value="">{t("admin:school_class_teacher_pick")}</option>
                  {activeTeachers.map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
                </select>
              </Field>
              <button data-testid="class-form-submit" type="submit" disabled={createClass.isPending} className={btnPrimary}>
                {t("admin:school_class_create")}
              </button>
            </form>
            {teachers.data && activeTeachers.length === 0 && (
              <p className="text-xs text-muted mt-2">{t("admin:school_class_need_teacher")}</p>
            )}
            {createClass.error && <div className="mt-3"><ErrorText>{errMsg(createClass.error, t("common:error_generic"))}</ErrorText></div>}
          </Card>
          <Card className="mt-3 overflow-x-auto">
            {classes.isLoading && <p className="text-muted p-4">{t("common:loading")}</p>}
            {classes.error && <div className="p-4"><ErrorText>{errMsg(classes.error, t("common:error_generic"))}</ErrorText></div>}
            {classes.data && classes.data.length === 0 && <p className="text-muted p-4 text-sm">{t("admin:school_no_classes")}</p>}
            {classes.data && classes.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">{t("admin:school_class_name")}</th>
                    <th className="text-left px-4 py-2">{t("admin:school_class_teacher")}</th>
                    <th className="text-right px-4 py-2">{t("admin:students")}</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.data.map((c) => (
                    <tr key={c.id} data-testid="class-row" className="border-b border-gray-100">
                      <td className="px-4 py-2 font-bold text-primary">{c.name}</td>
                      <td className="px-4 py-2">{c.teacher_name ?? "—"}</td>
                      <td className="px-4 py-2 text-right">{t("common:people", { n: c.learner_count ?? c.student_count ?? 0 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </Section>

        {/* 学習者の一括登録 */}
        <Section title={t("admin:school_csv_title")}>
          <Card className="p-4">
            <p className="text-xs text-muted mb-2">{t("admin:school_csv_help")}</p>
            <form onSubmit={(e) => { e.preventDefault(); if (csv.trim()) uploadCsv.mutate(csv); }}>
              <textarea
                data-testid="learners-csv"
                value={csv}
                onChange={(e) => setCsv(e.target.value)}
                rows={6}
                spellCheck={false}
                placeholder={"login_id,display_name,class\nstu-001,Anna Lee,NIHONGO101"}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm font-mono focus:border-primary focus:outline-none"
              />
              <div className="flex justify-end mt-2">
                <button data-testid="learners-csv-submit" type="submit" disabled={uploadCsv.isPending} className={btnPrimary}>
                  {uploadCsv.isPending ? t("common:loading") : t("admin:school_csv_submit")}
                </button>
              </div>
            </form>
            {csvError && <div className="mt-3"><ErrorText testId="learners-error">{csvError}</ErrorText></div>}
            {credentials && (
              <div className="mt-4">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                  <p className="text-sm text-green-700">{t("admin:school_csv_done", { n: credentials.length })}</p>
                  <button type="button" onClick={downloadCredentials} className={btnSecondary}>{t("admin:school_csv_download")}</button>
                </div>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-muted">
                      <tr>
                        <th className="text-left px-3 py-2">{t("admin:login_id")}</th>
                        <th className="text-left px-3 py-2">{t("admin:col_name")}</th>
                        <th className="text-left px-3 py-2">{t("admin:school_learner_class")}</th>
                        <th className="text-left px-3 py-2">{t("admin:school_csv_password")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credentials.map((c) => (
                        <tr key={c.login_id} data-testid="credential-row" className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono" data-testid="credential-login-id">{c.login_id}</td>
                          <td className="px-3 py-2">{c.display_name}</td>
                          <td className="px-3 py-2">{c.class || t("admin:school_no_class")}</td>
                          <td className="px-3 py-2 font-mono select-all" data-testid="credential-password">{c.password}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        </Section>

        {/* 学習者の一覧 */}
        <Section title={t("admin:school_learners")}>
          <Card className="overflow-x-auto">
            {learners.isLoading && <p className="text-muted p-4">{t("common:loading")}</p>}
            {learners.error && <div className="p-4"><ErrorText>{errMsg(learners.error, t("common:error_generic"))}</ErrorText></div>}
            {learners.data && learners.data.length === 0 && <p className="text-muted p-4 text-sm">{t("admin:school_no_learners")}</p>}
            {learners.data && learners.data.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">{t("admin:col_name")}</th>
                    <th className="text-left px-4 py-2">{t("admin:login_id")}</th>
                    <th className="text-left px-4 py-2">{t("admin:school_learner_class")}</th>
                    <th className="text-left px-4 py-2">{t("admin:org_status")}</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {learners.data.map((l) => {
                    const suspended = l.status === "suspended";
                    return (
                      <tr key={l.id} data-testid="learner-row" className="border-b border-gray-100">
                        <td className="px-4 py-2 font-bold text-primary">{l.display_name ?? l.name}</td>
                        <td className="px-4 py-2 font-mono text-xs">{l.login_id ?? ""}</td>
                        <td className="px-4 py-2">
                          <select
                            aria-label={t("admin:school_learner_class")}
                            value={l.class_id == null ? "" : String(l.class_id)}
                            disabled={updateLearner.isPending}
                            onChange={(e) => updateLearner.mutate({ id: l.id, body: { class_id: e.target.value ? Number(e.target.value) : null } })}
                            className="rounded border border-gray-300 px-2 py-1 text-sm bg-white"
                          >
                            <option value="">{t("admin:school_no_class")}</option>
                            {(classes.data ?? []).map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-2">{suspended ? <span className="text-accent">{t("common:status_suspended")}</span> : t("common:status_active")}</td>
                        <td className="px-4 py-2 text-right">
                          <button type="button" className={btnSecondary} disabled={updateLearner.isPending}
                            onClick={() => updateLearner.mutate({ id: l.id, body: { status: suspended ? "active" : "suspended" } })}>
                            {suspended ? t("admin:school_resume") : t("admin:school_suspend")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
          {updateLearner.error && <div className="mt-3"><ErrorText>{errMsg(updateLearner.error, t("common:error_generic"))}</ErrorText></div>}
        </Section>

        {/* 学校全体の進捗 */}
        <Section title={t("admin:school_progress")}>
          <Card className="overflow-x-auto">
            {progress.isLoading && <p className="text-muted p-4">{t("common:loading")}</p>}
            {progress.error && <div className="p-4"><ErrorText>{errMsg(progress.error, t("common:error_generic"))}</ErrorText></div>}
            {progress.data && progressRows.length === 0 && <p className="text-muted p-4 text-sm">{t("admin:school_no_progress")}</p>}
            {progressRows.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">{t("admin:school_learner_class")}</th>
                    <th className="text-right px-4 py-2">{t("admin:students")}</th>
                    <th className="text-right px-4 py-2">{t("admin:school_avg_level")}</th>
                    <th className="text-right px-4 py-2">{t("admin:school_last_studied")}</th>
                  </tr>
                </thead>
                <tbody>
                  {progressRows.map((p, i) => (
                    <tr key={p.class_id ?? `none-${i}`} className="border-b border-gray-100">
                      <td className="px-4 py-2">{p.class_name ?? p.name ?? t("admin:school_no_class")}</td>
                      <td className="px-4 py-2 text-right">{t("common:people", { n: p.learner_count ?? 0 })}</td>
                      <td className="px-4 py-2 text-right">{p.avg_level_label ?? levelLabel(p.avg_level)}</td>
                      <td className="px-4 py-2 text-right text-muted">{shortDate(p.last_studied_at)}</td>
                    </tr>
                  ))}
                  {progressTotal && (
                    <tr className="bg-gray-50 font-bold">
                      <td className="px-4 py-2">{t("admin:school_total")}</td>
                      <td className="px-4 py-2 text-right">{t("common:people", { n: progressTotal.learner_count ?? 0 })}</td>
                      <td className="px-4 py-2 text-right">{progressTotal.avg_level_label ?? levelLabel(progressTotal.avg_level)}</td>
                      <td className="px-4 py-2 text-right text-muted">{shortDate(progressTotal.last_studied_at)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </Card>
        </Section>
      </main>
    </div>
  );
}
